import axios from "axios";
import { createContext, useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";

export const notificationContext = createContext();

export const NotificationContextProvider = (props) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:3000";
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const getAllNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(backendUrl + "/api/notifications");
      if (data.success) {
        setNotifications(data.notifications);
        const unread = data.notifications.filter(notification => !notification.read);
        setUnreadCount(unread.length);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  const markAsRead = async (notificationId) => {
    try {
      const { data } = await axios.put(backendUrl + `/api/notifications/${notificationId}/read`);
      if (data.success) {
        setNotifications(prev => 
          prev.map(notification => 
            notification._id === notificationId 
              ? { ...notification, read: true }
              : notification
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { data } = await axios.put(backendUrl + "/api/notifications/read-all");
      if (data.success) {
        setNotifications(prev => 
          prev.map(notification => ({ ...notification, read: true }))
        );
        setUnreadCount(0);
        toast.success("All notifications marked as read!");
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const { data } = await axios.delete(backendUrl + `/api/notifications/${notificationId}`);
      if (data.success) {
        const notification = notifications.find(n => n._id === notificationId);
        setNotifications(prev => prev.filter(n => n._id !== notificationId));
        
        if (notification && !notification.read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
        
        toast.success("Notification deleted!");
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev]);
    if (!notification.read) {
      setUnreadCount(prev => prev + 1);
    }
  };

  const getUnreadNotifications = () => {
    return notifications.filter(notification => !notification.read);
  };

  const getNotificationsByType = (type) => {
    return notifications.filter(notification => notification.type === type);
  };

  // Disabled automatic notification fetching since backend doesn't have this endpoint
  // useEffect(() => {
  //   getAllNotifications();
  // }, []);

  const value = {
    backendUrl,
    notifications,
    setNotifications,
    unreadCount,
    setUnreadCount,
    loading,
    getAllNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    addNotification,
    getUnreadNotifications,
    getNotificationsByType,
  };

  return (
    <notificationContext.Provider value={value}>
      {props.children}
    </notificationContext.Provider>
  );
};